<template>
  <div class="nav-drawer-wrapper">
    <div class="nav-drawer-spacer"></div>
    <v-navigation-drawer
      class="custom-nav-drawer"
      :rail="!expanded"
      color="primary"
      permanent
      width="270"
    >
      <div class="logo-container">
        <img
          :src="rLogo"
          alt="R"
          class="r-logo"
          :class="{ 'collapsed': !expanded }"
        />
        <img
          :src="razayyaLogo"
          alt="Razayya"
          class="razayya-logo"
          :class="{ 'collapsed': !expanded }"
        />
        <img
          :src="financialLogo"
          alt="Financial"
          class="financial-logo"
          :class="{ 'collapsed': !expanded }"
        />
      </div>

      <v-spacer />

      <template v-if="featuredLinks?.length > 0">
        <v-list nav>
          <v-list-item
            v-for="item in featuredLinks"
            :key="item.to"
            :title="item.label"
            :to="item.to"
            @click="item.onClick"
          >
            <template v-slot:prepend>
              <v-icon v-if="item.icon" :icon="item.icon"></v-icon>
            </template>
          </v-list-item>
        </v-list>
      </template>

      <v-list density="compact" nav>
        <template v-for="item in currentModuleRoutes" :key="item.path">
          <v-list-group
            v-if="item.children"
            prepend-icon="item.meta.icon"
            :value="item.meta.expanded"
          >
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :title="item.meta.breadcrumb?.label || item.name"
              >
                <template v-slot:prepend>
                  <v-icon v-if="item.meta.icon" :icon="item.meta.icon"></v-icon>
                </template>
              </v-list-item>
            </template>

            <!-- child links now by name -->
            <v-list-item
              v-for="child in item.children"
              :key="child.name"
              :to="{ name: child.name }"
              :class="!expanded ? 'child-list-item-rail' : ''"
              :title="child.meta.breadcrumb?.label || child.name"
            >
              <template v-slot:prepend>
                <v-icon v-if="child.meta.icon" :icon="child.meta.icon"></v-icon>
              </template>
            </v-list-item>
          </v-list-group>

          <!-- top‑level link, also by name -->
          <v-list-item
            v-else
            :to="{ name: item.name }"
            :title="item.meta.breadcrumb?.label || item.name"
          >
            <template v-slot:prepend>
              <v-icon v-if="item.meta.icon" :icon="item.meta.icon"></v-icon>
            </template>
          </v-list-item>
        </template>
      </v-list>

      <template v-slot:append>
        <v-list nav density="compact">
          <v-list-item
            v-for="item in configurationLinks"
            :key="item.to"
            :title="item.label"
            :prepend-icon="item.icon"
            :to="item.to"
            @click="item.onClick"
          />
        </v-list>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script setup>
import rLogo from '@/assets/wg-r-logo.svg';
import razayyaLogo from '@/assets/gold-razayya-logo.svg';
import financialLogo from '@/assets/white-financial-logo.svg';

import {
  getModuleRoutes,
  getConfigurationNavItems,
  getFeatureNavItems
} from '@/registerModules';
import { ref, watch, computed } from 'vue';
import { useModuleStore } from '@/stores/moduleStore';
import { useAuthStore, useMetricsStore } from '@/stores';

const props = defineProps({ expanded: Boolean });
const authStore = useAuthStore();
const { hasPermission } = authStore;
const metricsStore = useMetricsStore();

const { getCurrentModule } = useModuleStore();
const currentViewBox = ref('0 100 576 350');

watch(
  () => props.expanded,
  (newVal) => {
    if (!newVal) {
      setTimeout(() => {
        currentViewBox.value = '180 115 200 200';
      }, 150);
    } else {
      currentViewBox.value = '0 100 576 350';
    }
  }
);

const moduleRoutes = getModuleRoutes();

// Dynamic children lookup - maps dynamicChildren key to store value
const dynamicChildrenFlags = computed(() => ({
  hasManualMetrics: metricsStore.hasManualMetrics
}));

const currentModuleRoutes = computed(() => {
  const currentModule = getCurrentModule();
  return moduleRoutes
    .filter((r) => r.meta?.module === currentModule)
    .flatMap((modRoute) =>
      modRoute.children
        .filter((c) => c.meta?.showInNav)
        .filter((c) =>
          !c.meta.permissions?.length ||
          c.meta.permissions.some((p) => hasPermission(p))
        )
        .map((route) => {
          // Handle dynamic children visibility
          const dynamicKey = route.meta?.dynamicChildren;
          if (dynamicKey && !dynamicChildrenFlags.value[dynamicKey]) {
            // Strip children if dynamic flag is false
            const { children, ...routeWithoutChildren } = route;
            return routeWithoutChildren;
          }
          // Keep children, but filter by showInNav and permissions
          if (route.children) {
            return {
              ...route,
              children: route.children
                .filter((c) => c.meta?.showInNav)
                .filter((c) =>
                  !c.meta.permissions?.length ||
                  c.meta.permissions.some((p) => hasPermission(p))
                )
            };
          }
          return route;
        })
    );
});

const configurationLinks = computed(() => {
  const currentModuleName = getCurrentModule();
  return getConfigurationNavItems(currentModuleName).filter((item) =>
    !item.permissions?.length ||
    item.permissions.some((p) => hasPermission(p))
  );
});

const featuredLinks = computed(() => {
  const currentModuleName = getCurrentModule();
  return getFeatureNavItems(currentModuleName).filter((item) =>
    !item.permissions?.length ||
    item.permissions.some((p) => hasPermission(p))
  );
});
</script>

<style>
.logo-svg {
  width: 100%;
  height: auto;
  transition: viewBox 1s ease;
}

/* Custom Navigation Drawer Style */
.nav-drawer-wrapper {
  display: flex;
}

.nav-drawer-spacer {
  width: 6px; /* Adjust the width as needed */
  /* background-color: #FFC655; /* secondary color from Vuetify theme */
  background-color: #FFC655; /* primary color from Vuetify theme */
  left: 0px;
  z-index: 1008;
  transform: translateX(0%);
  position: fixed;
  height: calc(100% + 0px);
  top: 0px;
  bottom: 0px;
}

.custom-nav-drawer {
  position: relative;
  left: 6px !important;
}

  .v-navigation-drawer--rail .v-list-group__items .v-list-item {
      padding-inline-start: 8px !important;
  }

  .logo-container {
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.r-logo {
  width: 4vh;
  max-width: 4vh;
  min-width: 4vh;
  transition: all 0.3s ease;
}

.r-logo.collapsed {
  width: 4vh;
  min-width: 3vh;
  transition: all 0.3s ease;
}

.razayya-logo, .financial-logo {
  width: 16vh;
  max-width: 16vh;
  min-width: 16vh;
  opacity: 1;
  padding: 3px 0;
  transition: all 0.3s ease;
}

.razayya-logo.collapsed, .financial-logo.collapsed {
  width: 0vh;
  max-width: 0px;
  min-width: 0px;
  opacity: 0;
  padding: 0;
}
</style>